import './SearchResultItem.scss'

import { IconCategory } from '@here/hds-iconlibrary/build/hds-iconlibrary'
import { HDSButton, HDSIcon, HDSTooltip } from '@here/hds-react-components'
import classNames from 'classnames'
import ListItem from 'components/App/components/ListItem'
import SvgPdsMarkerIcon from 'components/Map/components/SvgPdsMarkerIcon'
import { SvgPdsIcon } from 'components/SvgIcon'
import { TripAdvisorRating } from 'components/TripAdvisorRating'
import { PageType, useNavigation } from 'lib/hooks'
import { i18n } from 'lib/i18n'
import { clearStringAddress } from 'lib/place'
import React from 'react'

export type ResultKind = 'autosuggest' | 'search' | 'recent' | 'none'

export interface SearchResultItemProps {
  iconName?: string
  iconCategory?: string
  iconColor?: string
  categoryId?: string
  kind: ResultKind
  link: string
  title: string
  highlightedSubtitle?: string
  subtitle?: string
  thumbnail?: string
  averageRating?: number
  reviewCount?: number
  isHighlighted: boolean
  onMouseDown?: () => void
  onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  onDeleteItem?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
}

const iconColorDefault = 'var(--hds-poi-focus-generic)'

function getHdsIconStyle(color: string): React.CSSProperties {
  return {
    '--hds-icon-color': color,
  } as React.CSSProperties
}

function getSearchIconOrThumbnail(
  thumbnail: string | undefined,
  iconName: string | undefined,
  categoryId: string | undefined
): JSX.Element {
  if (thumbnail !== undefined) {
    return <img src={thumbnail} className="SearchResultItem__thumbnail" />
  }

  return (
    <div className="SearchResultItem__icon-container">
      <SvgPdsMarkerIcon
        className="SearchResultItem__icon"
        categoryId={categoryId}
        innerColor="white"
      />
    </div>
  )
}

function getAutosuggestIcon(
  iconName: string | undefined,
  iconCategory: string | undefined,
  iconColor: string | undefined,
  categoryId: string | undefined
): JSX.Element {
  return (
    <div className="SearchResultItem__icon-container">
      {categoryId !== undefined ? (
        <SvgPdsIcon className="SearchResultItem__icon" categoryId={categoryId} />
      ) : (
        <div className="SearchResultItem__icon">
          <HDSIcon
            name={iconName !== undefined ? iconName : 'point'}
            category={iconCategory !== undefined ? (iconCategory as IconCategory) : 'poi'}
            size="18"
            style={getHdsIconStyle(iconColor !== undefined ? iconColor : iconColorDefault)}
          />
        </div>
      )}
    </div>
  )
}

export const SearchResultItem = ({
  iconName,
  iconCategory,
  iconColor,
  categoryId,
  kind,
  link,
  title,
  highlightedSubtitle,
  subtitle,
  thumbnail,
  averageRating,
  reviewCount,
  isHighlighted,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
  onDeleteItem,
}: SearchResultItemProps): JSX.Element => {
  const { goTo } = useNavigation()
  const handleLinkClick = (): void => {
    const pageType = link.startsWith('/discover/') ? PageType.DISCOVERY : PageType.PDC
    goTo({
      type: pageType,
      url: link,
    })
  }
  const showDelete = isHighlighted && kind === 'recent' && iconName !== 'current-location'

  // an icon container is always shown (even when there's no icon) to have consistent offset
  return (
    <ListItem
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={classNames('SearchResultItemContainer', `SearchResultItem--${kind}`, {
        'SearchResultItem--highlighted': isHighlighted,
      })}
      onClick={onClick}
      onMouseDown={() => onMouseDown && onMouseDown()}
    >
      <div
        className={classNames('SearchResultItem__link', {
          'SearchResultItem__link--highlighted': isHighlighted,
        })}
        onClick={handleLinkClick}
      >
        {(kind === 'autosuggest' || kind === 'recent') &&
          getAutosuggestIcon(iconName, iconCategory, iconColor, categoryId)}
        {kind === 'search' && getSearchIconOrThumbnail(thumbnail, iconName, categoryId)}

        <div className="SearchResultItem__details">
          <div className="SearchResultItem__title">{title}</div>

          {(subtitle !== undefined || highlightedSubtitle !== undefined) && (
            <div className="SearchResultItem__subtitles">
              {highlightedSubtitle !== undefined && (
                <span className="SearchResultItem__subtitle--highlighted">
                  {highlightedSubtitle}
                </span>
              )}
              {subtitle !== undefined && (
                <span className="SearchResultItem__subtitle">
                  {clearStringAddress(subtitle, title)}
                </span>
              )}
            </div>
          )}

          {averageRating !== undefined && reviewCount !== undefined && (
            <TripAdvisorRating value={averageRating} reviewCount={reviewCount} />
          )}
        </div>
      </div>
      {showDelete && (
        <HDSTooltip position="bottom" content={i18n('search.recent.delete')}>
          <HDSButton
            className="SearchResultItem__delete"
            variant="subtle"
            size="small"
            icon="cross"
            onMouseDown={onDeleteItem}
          />
        </HDSTooltip>
      )}
    </ListItem>
  )
}
